// AI Approvals — the human-in-the-loop queue. Crucial AI actions parked
// under the `approve` automation mode (resolve / cancel / delete, or any
// category a SUPER_ADMIN set to `approve`) land here for a human to
// approve (executes it) or reject. Data: GET /admin/ai/approvals;
// decisions: POST /admin/ai/approvals/{id}/{approve,reject}.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { fmtRelative } from "@/lib/format";

interface PendingAction {
  id: string;
  admin_email: string;
  tool: string;
  category: string;
  status: string;
  preview: unknown;
  created_at: string;
  action_path: string;
}

export const Route = createFileRoute("/_app/ai/approvals")({
  component: ApprovalsPage,
});

function categoryTone(c: string): "danger" | "warning" | "accent" {
  if (c === "delete" || c === "cancel" || c === "resolve") return "danger";
  if (c === "publish" || c === "bulk_publish") return "warning";
  return "accent";
}

function ApprovalsPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["ai", "approvals"],
    queryFn: () => api<{ pending: PendingAction[] }>("/admin/ai/approvals"),
    refetchInterval: 15_000,
  });
  const [rejecting, setRejecting] = useState<PendingAction | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["ai", "approvals"] });

  const approve = useMutation({
    mutationFn: (id: string) => api(`/admin/ai/approvals/${id}/approve`, { method: "POST" }),
    onSuccess: invalidate,
    meta: { successMessage: "Action approved and executed." },
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api(`/admin/ai/approvals/${id}/reject`, { method: "POST", body: { reason } }),
    onSuccess: async () => {
      setRejecting(null);
      await invalidate();
    },
    meta: { successMessage: "Action rejected." },
  });

  const pending = (q.data?.pending ?? []).filter((p) => p.status === "PENDING");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-t-1">AI Approvals</h1>
        <p className="text-sm text-muted">
          Crucial actions an AI assistant requested that need a human to approve before they
          take effect. Approving executes the action as the original requester.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Awaiting approval</CardTitle>
        </CardHeader>
        <CardBody>
          {q.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : pending.length === 0 ? (
            <EmptyState
              title="Nothing to approve"
              description="AI actions that require human approval will appear here."
            />
          ) : (
            <div className="space-y-3">
              {pending.map((p) => (
                <div
                  key={p.id}
                  className="flex flex-col gap-3 rounded-lg border border-line p-4 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge tone={categoryTone(p.category)}>{p.category}</Badge>
                      <span className="font-mono text-sm text-t-1">{p.tool}</span>
                    </div>
                    <div className="text-xs text-muted">
                      requested by {p.admin_email || "—"} · {fmtRelative(p.created_at)}
                    </div>
                    {p.preview != null && (
                      <pre className="mt-1 max-w-full overflow-x-auto rounded bg-surface-2 p-2 text-xs text-t-2">
                        {JSON.stringify(p.preview, null, 2)}
                      </pre>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      onClick={() => approve.mutate(p.id)}
                      disabled={approve.isPending}
                    >
                      Approve
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setRejecting(p)}>
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <ConfirmModal
        open={rejecting !== null}
        title="Reject this action?"
        tone="danger"
        confirmLabel="Reject"
        description={
          rejecting ? (
            <span>
              The AI's <span className="font-mono">{rejecting.tool}</span> request will be
              discarded and not executed.
            </span>
          ) : undefined
        }
        reasonLabel="Reason (optional)"
        reasonPlaceholder="why this is being rejected"
        loading={reject.isPending}
        error={reject.isError ? (reject.error as Error).message : null}
        onCancel={() => setRejecting(null)}
        onConfirm={(reason) => rejecting && reject.mutate({ id: rejecting.id, reason })}
      />
    </div>
  );
}
